import React from 'react'
import classNames from 'classnames'
import { isIOS, isMobileSafari } from 'react-device-detect'
import { Case, Switch } from 'react-if'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { ParagraphMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { GuildSignupSingleShowLayout } from '@/services/GuildSignupPageService/GuildSignupPageTypes'
import { buildCloudinaryImagePathFromContentfulObject } from '@/utils/Cloudinary'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { GuildCTAButton } from '@/views/GuildSignupView/GuildCTAButton'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'
import { AlreadyAGuildMember } from '@/views/GuildSignupView/GuildSingleShowLayout/GuildHeroCTA/AlreadyAGuildMember'

export interface GuildHeroCTAProps {
  fullWidth?: boolean
}

export const GuildHeroCTA: React.FC<GuildHeroCTAProps> = ({ fullWidth }) => {
  const { emphasis, layout } = useGuildSignupContext()
  const { project } = layout as GuildSignupSingleShowLayout
  const { logo, name: filmName } = project
  const { t } = useTranslate('guild')

  const mobileAdjustment = isIOS && isMobileSafari

  const defaultTicketsLeadingText = t(
    'claimYourTickets',
    'See {{ filmName }} for free! Join the Angel Guild to get two tickets now.',
    { filmName },
  )

  const defaultEarlyAccessLeadingText = t(
    'streamingExclusivelyForGuild',
    'Streaming exclusively for Guild Members through the Angel Guild Membership.',
  )

  return (
    <div className="relative">
      <div
        className={classNames(
          'absolute inset-x-0 sm:bottom-8 sm:max-w-[450px] bg-white/10 px-3 py-8 !backdrop-blur-2xl sm:left-1/2 sm:-translate-x-1/2',
          fullWidth ? '' : 'mx-8 md:mx-4 mb-4 rounded-3xl',
          mobileAdjustment ? 'bottom-[46px]' : isIOS ? 'bottom-[8px]' : 'bottom-[66px]',
        )}
      >
        <div className="flex flex-col items-center">
          <div className="mb-4 max-w-[250px]">
            <Image
              alt={filmName}
              src={buildCloudinaryImagePathFromContentfulObject(logo)}
              height={logo?.[0]?.height}
              width={logo?.[0]?.width}
            />
          </div>
          <div className="mb-2 text-center text-gray-300">
            <ParagraphMD className="max-w-[320px] !font-light">
              <Switch>
                <Case condition={emphasis === 'early-access'}>{defaultEarlyAccessLeadingText}</Case>
                <Case condition={emphasis === 'tickets'}>{defaultTicketsLeadingText}</Case>
              </Switch>
            </ParagraphMD>
          </div>

          <GuildCTAButton className="mx-auto shadow-dark-3" />

          <AlreadyAGuildMember />

          <div className="-mb-4 mt-3 text-center text-[8px] text-gray-300">
            <Translate t={t} i18nKey={'byBecomingAGuildMemberYouAgreeToTerms'}>
              By becoming a Guild Member you agree to the
              <InternalLink
                className="mx-1 cursor-pointer text-oxide hover:opacity-80"
                href={paths.legal.guildMembership}
              >
                Terms and Conditions
              </InternalLink>
              and
              <InternalLink
                className="ml-1 cursor-pointer text-oxide hover:opacity-80"
                href={paths.legal.privacyPolicy}
              >
                Privacy Policy
              </InternalLink>
              .
            </Translate>
          </div>
        </div>
      </div>
    </div>
  )
}
